/** @jsxImportSource preact */
import { h } from 'preact';
import { useState, useEffect, useCallback, useMemo, useRef } from 'preact/hooks';
import { IncidentDetailModal, IncidentDetail } from './IncidentDetailModal';
import { formatTaxonomyLabel } from '../lib/taxonomyFormat';
import { NatSecIncidentDetailModal, ImpactPill } from './NatSecIncidentDetailModal';
import { incidentPageUrl } from '../lib/incidentPages';
import { IMPACT_LABELS } from '../data/impactScale';
import { readIncidentParam, pushIncident, clearIncident } from '../lib/incidentUrl';
import type { IncidentListItem } from './IncidentListModal';
import { IncidentFilterBar, FilterDim } from './IncidentFilterBar';
import type { StackByDim } from './IncidentChart';

export type IncidentViewMode = 'standard' | 'natsec';

/** Runtime controller — mirrors IncidentChartController so the embed's
 * GovCharts.applyFilters() can drive a mounted IncidentView the same way
 * it drives the bar/pie charts. Each call resets state to the filters
 * supplied (i.e. acts as a preset application, not an additive update). */
export interface IncidentViewController {
  applyPreset: (filters: Record<string, unknown>) => void;
  resetFilters: () => void;
}

const incidentViewRegistry = new Map<string, IncidentViewController>();

export function getIncidentViewController(id: string): IncidentViewController | undefined {
  return incidentViewRegistry.get(id);
}

export interface IncidentViewProps {
  apiBase: string;
  containerId: string;
  /**
   * Initial filter values, keyed by filter id (year, domain, subdomain,
   * entity, intent, timing, euRisk, aiGoal, aiModel, aiDeveloper,
   * natSecImminence, natSecAutonomy, natSecNovelty, minYear, minSeverity*,
   * sortBy, sortDir).
   * Categorical filters accept either a single value or an array — arrays
   * surface as multi-select chips (e.g. `natSecImminence: ['Near-term',
   * 'Immediate']`). Scalar filters (severity thresholds, sort) ignore all
   * but the first entry of an array.
   */
  initialFilters?: Record<string, string | string[]>;
  /** Year floor (e.g. 2015) — matches IncidentChart's minYear prop */
  minYear?: number;
  /** Incidents dataset to serve from (API `src` param, e.g.
   * 'incidents-arcola'). Omit for the default dataset. */
  dataset?: string;
  /**
   * 'standard' (default) renders the existing incident list + detail modal.
   * 'natsec' fetches the National Security columns/payload, defaults the
   * Overall NatSec Score filter to 4 (Severe), and opens the NatSec
   * detail modal on row click.
   */
  mode?: IncidentViewMode;
}

const MOBILE_BREAKPOINT = 640;

// Only one IncidentView per page mirrors its modal in the URL (the first to
// mount); a second instance, as on the embed-test page, opens modals locally.
let urlSyncOwner: string | null = null;

interface IncidentListResponse {
  items?: IncidentListItem[];
  totalIncidents?: number;
  totalIncidentsAll?: number;
  years?: string[];
  domains?: string[];
  subdomains?: string[];
  entities?: string[];
  intents?: string[];
  timings?: string[];
  euRisks?: string[];
  aiGoals?: string[];
  /** Subject AI-system identity (Arcola mirror): registry-normalised model
   * families / developers plus the three state labels; `hasAiIdentity`
   * gates the filters and the AI System column. */
  aiModels?: string[];
  aiDevelopers?: string[];
  hasAiIdentity?: boolean;
  natSecImminenceLevels?: string[];
  natSecAutonomyLevels?: string[];
  natSecNoveltyLevels?: string[];
  severityFields?: Array<{ key: string; label: string }>;
}

// Mirrors STACK_BY_LABELS in IncidentChart so dimension chips in the IncidentView
// filter bar use the same labels as on the charts.
const DIM_LABELS: Record<StackByDim, string> = {
  domain: 'Domain',
  subdomain: 'Subdomain',
  allSubdomains: 'All Subdomains',
  harmCategory: 'Harm category',
  entity: 'Entity',
  intent: 'Intent',
  timing: 'Timing',
  aiGoal: 'AI Purpose',
  euRisk: 'EU AI Act Risk Tier',
  aiModel: 'AI Model Family',
  aiDeveloper: 'AI Developer',
  // highestSeverity / natSecImpact are stack-by-only dimensions on the
  // bar/pie charts — they don't currently appear as filterable chips on
  // the IncidentView, but the Record type requires every key to be set.
  highestSeverity: 'Highest severity',
  natSecImpact: 'NatSec Overall Impact',
};

const DIM_TOOLTIPS: Partial<Record<StackByDim, string>> = {
  euRisk: 'Risk tier as defined by the EU AI Act',
  aiModel: 'The model family of the AI system(s) the incident report names (e.g. GPT, Claude, Gemini), normalised against Arcola’s registry. "Unidentified AI system" = the report evidences AI but does not identify it; "No AI system named" = none is mentioned; "Not assessed" = no identity was derived.',
  aiDeveloper: 'Who built the AI system(s) the incident report names, normalised against Arcola’s registry. Recorded only when the report says so. Distinct from the AIID "Alleged developer" field.',
};

// Mirrors IncidentChart's helper for layering the host-side minYear prop with
// the user's own "Year ≥" filter.
function computeEffectiveMinYear(prop: number | undefined, userFilter: string[]): number | null {
  const userVal = userFilter.length > 0 ? parseInt(userFilter[0], 10) : NaN;
  const candidates: number[] = [];
  if (typeof prop === 'number' && Number.isFinite(prop)) candidates.push(prop);
  if (Number.isFinite(userVal)) candidates.push(userVal);
  if (candidates.length === 0) return null;
  return Math.max(...candidates);
}

// initialFilters values are `string | string[]`. Categorical seedings
// normalise either shape to a string[]; scalar seedings pick the first.
const toArr = (v: string | string[] | undefined): string[] => {
  if (v == null) return [];
  return Array.isArray(v) ? v : [v];
};
const firstStr = (v: string | string[] | undefined): string | undefined => {
  if (v == null) return undefined;
  return Array.isArray(v) ? v[0] : v;
};

export function IncidentView({ apiBase, containerId, initialFilters = {}, minYear, mode = 'standard', dataset }: IncidentViewProps) {
  const isNatSec = mode === 'natsec';
  const [isMobile, setIsMobile] = useState(false);

  const [yearFilter, setYearFilter] = useState<string[]>(toArr(initialFilters.year));
  const [minYearFilter, setMinYearFilter] = useState<string[]>([]);
  const [domainFilter, setDomainFilter] = useState<string[]>(toArr(initialFilters.domain));
  const [subdomainFilter, setSubdomainFilter] = useState<string[]>(toArr(initialFilters.subdomain));
  const [entityFilter, setEntityFilter] = useState<string[]>(toArr(initialFilters.entity));
  const [intentFilter, setIntentFilter] = useState<string[]>(toArr(initialFilters.intent));
  const [timingFilter, setTimingFilter] = useState<string[]>(toArr(initialFilters.timing));
  const [euRiskFilter, setEuRiskFilter] = useState<string[]>(toArr(initialFilters.euRisk));
  const [aiGoalFilter, setAiGoalFilter] = useState<string[]>(toArr(initialFilters.aiGoal));
  // Subject AI-system identity filters (Arcola mirror only). Values are
  // registry labels or one of the three state labels — NOT stable keys
  // (Arcola re-derives them as its registry grows), so presets should not
  // bake them in.
  const [aiModelFilter, setAiModelFilter] = useState<string[]>(toArr(initialFilters.aiModel));
  const [aiDeveloperFilter, setAiDeveloperFilter] = useState<string[]>(toArr(initialFilters.aiDeveloper));
  // NatSec threat-characteristic filters (categorical, multi-select).
  const [natSecImminenceFilter, setNatSecImminenceFilter] = useState<string[]>(toArr(initialFilters.natSecImminence));
  const [natSecAutonomyFilter, setNatSecAutonomyFilter] = useState<string[]>(toArr(initialFilters.natSecAutonomy));
  const [natSecNoveltyFilter, setNatSecNoveltyFilter] = useState<string[]>(toArr(initialFilters.natSecNovelty));
  // Severity filter mirrors IncidentChart: map of param key → minimum score.
  // `minSeverityHighest` is the "any category ≥ N" shortcut used in the popover.
  // Seeded from initialFilters so preset embeds (e.g. "severe or catastrophic
  // malicious-actor incidents") can ship a pre-applied severity floor.
  const [severityFilters, setSeverityFilters] = useState<Record<string, number>>(() => {
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(initialFilters)) {
      if (k.startsWith('minSeverity')) {
        const raw = firstStr(v);
        const n = raw ? parseInt(raw, 10) : NaN;
        if (Number.isFinite(n) && n > 0) out[k] = n;
      }
    }
    return out;
  });

  // NatSec severity filters — mirrors `severityFilters` for the standard
  // Incident View. Keys are URL param names (minNatSecOverall and the five
  // per-category dimensions). NatSec mode seeds Overall ≥ 4 (Severe) so the
  // initial view is the assessment-relevant subset; initialFilters can
  // override to 0 or any other value 1–5, and the user can dial in
  // per-category overrides via the popover.
  const [natSecSeverityFilters, setNatSecSeverityFilters] = useState<Record<string, number>>(() => {
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(initialFilters)) {
      if (k.startsWith('minNatSec')) {
        const raw = firstStr(v);
        const n = raw ? parseInt(raw, 10) : NaN;
        if (Number.isFinite(n) && n > 0) out[k] = n;
      }
    }
    // Default to Overall ≥ 4 in NatSec mode unless the caller already set
    // a NatSec filter explicitly (including minNatSecOverall=0 to clear).
    if (isNatSec && !('minNatSecOverall' in out) && initialFilters.minNatSecOverall === undefined) {
      out.minNatSecOverall = 4;
    }
    return out;
  });

  // Filter dropdown options (populated from API response)
  const [yearOptions, setYearOptions] = useState<string[]>([]);
  const [domainOptions, setDomainOptions] = useState<string[]>([]);
  const [subdomainOptions, setSubdomainOptions] = useState<string[]>([]);
  const [entityOptions, setEntityOptions] = useState<string[]>([]);
  const [intentOptions, setIntentOptions] = useState<string[]>([]);
  const [timingOptions, setTimingOptions] = useState<string[]>([]);
  const [euRiskOptions, setEuRiskOptions] = useState<string[]>([]);
  const [aiGoalOptions, setAiGoalOptions] = useState<string[]>([]);
  const [aiModelOptions, setAiModelOptions] = useState<string[]>([]);
  const [aiDeveloperOptions, setAiDeveloperOptions] = useState<string[]>([]);
  // Whether the dataset carries identity cells — gates the two filters and
  // the "AI System" column (the legacy base has none).
  const [hasAiIdentity, setHasAiIdentity] = useState(false);
  const [natSecImminenceOptions, setNatSecImminenceOptions] = useState<string[]>([]);
  const [natSecAutonomyOptions, setNatSecAutonomyOptions] = useState<string[]>([]);
  const [natSecNoveltyOptions, setNatSecNoveltyOptions] = useState<string[]>([]);
  const [severityFields, setSeverityFields] = useState<Array<{ key: string; label: string }>>([]);

  const [searchQuery, setSearchQuery] = useState('');
  type SortField =
    | 'incidentId'
    | 'title'
    | 'date'
    | 'subdomain'
    | 'aiSystem'
    | 'highestSeverityScore'
    | 'natSecResponseLevel'
    | 'natSecOverallScore';
  const VALID_SORT_FIELDS: ReadonlyArray<SortField> = [
    'incidentId', 'title', 'date', 'subdomain', 'aiSystem',
    'highestSeverityScore', 'natSecResponseLevel', 'natSecOverallScore',
  ];
  // Initial sort can be set from a preset filter config — e.g.
  // `{ sortBy: 'highestSeverityScore', sortDir: 'desc' }` to lead with
  // the most severe incidents. Falls back to DATE DESCENDING so the table
  // opens on the most recent incidents (undated rows sort last — see the
  // date branch of the comparator). A preset must opt into 'asc'
  // explicitly.
  const initialSortBy = firstStr(initialFilters.sortBy);
  const initialSortDir = firstStr(initialFilters.sortDir);
  const [sortBy, setSortBy] = useState<SortField>(
    initialSortBy && (VALID_SORT_FIELDS as readonly string[]).includes(initialSortBy)
      ? (initialSortBy as SortField)
      : 'date'
  );
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(
    initialSortDir === 'asc' ? 'asc' : 'desc'
  );

  const parseDateForSort = (raw: string): number => {
    if (!raw) return Number.POSITIVE_INFINITY;
    const dmy = raw.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
    if (dmy) {
      const [, d, m, y] = dmy;
      return Date.UTC(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
    }
    const t = Date.parse(raw);
    return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
  };

  const [incidents, setIncidents] = useState<IncidentListItem[]>([]);
  const [totalIncidentsAll, setTotalIncidentsAll] = useState<number | null>(null);
  const [listLoading, setListLoading] = useState(true);

  // Detail modal
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailId, setDetailId] = useState('');
  const [detailIncident, setDetailIncident] = useState<IncidentDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    const update = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // Fetch list whenever filters change
  useEffect(() => {
    let cancelled = false;
    setListLoading(true);

    const qs = new URLSearchParams();
    if (dataset) qs.set('src', dataset);
    qs.set('chartType', isNatSec ? 'natsec-incident-list' : 'incident-list');
    const effectiveMinYear = computeEffectiveMinYear(minYear, minYearFilter);
    if (effectiveMinYear !== null) qs.set('minYear', String(effectiveMinYear));
    // Use repeated params so values containing commas survive transport.
    for (const v of yearFilter) qs.append('year', v);
    for (const v of domainFilter) qs.append('domain', v);
    for (const v of subdomainFilter) qs.append('subdomain', v);
    for (const v of entityFilter) qs.append('entity', v);
    for (const v of intentFilter) qs.append('intent', v);
    for (const v of timingFilter) qs.append('timing', v);
    for (const v of euRiskFilter) qs.append('euRisk', v);
    for (const v of aiGoalFilter) qs.append('aiGoal', v);
    for (const v of aiModelFilter) qs.append('aiModel', v);
    for (const v of aiDeveloperFilter) qs.append('aiDeveloper', v);
    if (isNatSec) {
      for (const v of natSecImminenceFilter) qs.append('natSecImminence', v);
      for (const v of natSecAutonomyFilter) qs.append('natSecAutonomy', v);
      for (const v of natSecNoveltyFilter) qs.append('natSecNovelty', v);
    }
    for (const [key, val] of Object.entries(severityFilters)) {
      if (val > 0) qs.set(key, String(val));
    }
    if (isNatSec) {
      for (const [key, val] of Object.entries(natSecSeverityFilters)) {
        if (val > 0) qs.set(key, String(val));
      }
    }

    fetch(`${apiBase}/data/incidents?${qs}`)
      .then((r) => r.json())
      .then((json: IncidentListResponse) => {
        if (cancelled) return;
        setIncidents(json.items ?? []);
        if (json.totalIncidentsAll != null) setTotalIncidentsAll(json.totalIncidentsAll);
        if (json.years) setYearOptions(json.years);
        if (json.domains) setDomainOptions(json.domains);
        if (json.subdomains) setSubdomainOptions(json.subdomains);
        if (json.entities) setEntityOptions(json.entities);
        if (json.intents) setIntentOptions(json.intents);
        if (json.timings) setTimingOptions(json.timings);
        if (json.euRisks) setEuRiskOptions(json.euRisks);
        if (json.aiGoals) setAiGoalOptions(json.aiGoals);
        if (json.aiModels) setAiModelOptions(json.aiModels);
        if (json.aiDevelopers) setAiDeveloperOptions(json.aiDevelopers);
        setHasAiIdentity(Boolean(json.hasAiIdentity));
        if (json.natSecImminenceLevels) setNatSecImminenceOptions(json.natSecImminenceLevels);
        if (json.natSecAutonomyLevels) setNatSecAutonomyOptions(json.natSecAutonomyLevels);
        if (json.natSecNoveltyLevels) setNatSecNoveltyOptions(json.natSecNoveltyLevels);
        if (json.severityFields) setSeverityFields(json.severityFields);
      })
      .catch((err) => console.error('Failed to fetch incidents:', err))
      .finally(() => { if (!cancelled) setListLoading(false); });

    return () => { cancelled = true; };
  }, [apiBase, dataset, isNatSec, minYear, minYearFilter.join(','), yearFilter.join(','), domainFilter.join(','), subdomainFilter.join(','), entityFilter.join(','), intentFilter.join(','), timingFilter.join(','), euRiskFilter.join(','), aiGoalFilter.join(','), aiModelFilter.join(','), aiDeveloperFilter.join(','), natSecImminenceFilter.join(','), natSecAutonomyFilter.join(','), natSecNoveltyFilter.join(','), JSON.stringify(severityFilters), JSON.stringify(natSecSeverityFilters)]);

  const openDetail = useCallback((incidentId: string) => {
    setDetailId(incidentId);
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailIncident(null);

    const qs = new URLSearchParams();
    if (dataset) qs.set('src', dataset);
    qs.set('chartType', isNatSec ? 'natsec-incident-detail' : 'incident-detail');
    qs.set('incidentId', incidentId);

    fetch(`${apiBase}/data/incidents?${qs}`)
      .then((r) => r.json())
      .then((json: { incident?: IncidentDetail }) => {
        setDetailIncident(json?.incident ?? null);
      })
      .catch((err) => console.error('Failed to fetch incident detail:', err))
      .finally(() => setDetailLoading(false));
  }, [apiBase, dataset, isNatSec]);

  // The open modal is mirrored in the URL (?incident=<id>) so it can be
  // shared and Back closes it. See lib/incidentUrl.
  const ownsUrl = useRef(false);

  const handleIncidentClick = useCallback((incidentId: string) => {
    openDetail(incidentId);
    if (ownsUrl.current) pushIncident(incidentId);
  }, [openDetail]);

  const handleDetailClose = useCallback(() => {
    setDetailOpen(false);
    if (ownsUrl.current) clearIncident();
  }, []);

  useEffect(() => {
    if (urlSyncOwner !== null) return;
    urlSyncOwner = containerId;
    ownsUrl.current = true;
    const syncFromUrl = () => {
      const id = readIncidentParam(window.location.search);
      if (id) openDetail(id);
      else setDetailOpen(false);
    };
    const initial = readIncidentParam(window.location.search);
    if (initial) openDetail(initial);
    window.addEventListener('popstate', syncFromUrl);
    return () => {
      window.removeEventListener('popstate', syncFromUrl);
      urlSyncOwner = null;
      ownsUrl.current = false;
    };
  }, [openDetail, containerId]);

  const handleReset = () => {
    setYearFilter([]);
    setMinYearFilter([]);
    setDomainFilter([]);
    setSubdomainFilter([]);
    setEntityFilter([]);
    setIntentFilter([]);
    setTimingFilter([]);
    setEuRiskFilter([]);
    setAiGoalFilter([]);
    setAiModelFilter([]);
    setAiDeveloperFilter([]);
    setNatSecImminenceFilter([]);
    setNatSecAutonomyFilter([]);
    setNatSecNoveltyFilter([]);
    setSeverityFilters({});
    // NatSec view: reset back to the canonical default (Overall ≥ 4), not
    // an empty map — 0 would show every record and obscure the assessment
    // subset that's the whole point of the view.
    setNatSecSeverityFilters(isNatSec ? { minNatSecOverall: 4 } : {});
    setSearchQuery('');
  };

  // Register the view in the runtime controller registry so the embed's
  // GovCharts.applyFilters() can drive this mounted view from a preset
  // selector on the host page — mirrors IncidentChartController.
  useEffect(() => {
    const toStrFromAny = (v: unknown): string | undefined => {
      if (v == null) return undefined;
      if (Array.isArray(v)) return v[0] != null ? String(v[0]) : undefined;
      return String(v);
    };
    const toArrFromAny = (v: unknown): string[] => {
      if (v == null) return [];
      if (Array.isArray(v)) return v.map((x) => String(x));
      return [String(v)];
    };
    const controller: IncidentViewController = {
      applyPreset: (filters) => {
        setYearFilter(toArrFromAny(filters.year));
        setMinYearFilter(filters.minYear != null ? [String(filters.minYear)] : []);
        setDomainFilter(toArrFromAny(filters.domain));
        setSubdomainFilter(toArrFromAny(filters.subdomain));
        setEntityFilter(toArrFromAny(filters.entity));
        setIntentFilter(toArrFromAny(filters.intent));
        setTimingFilter(toArrFromAny(filters.timing));
        setEuRiskFilter(toArrFromAny(filters.euRisk));
        setAiGoalFilter(toArrFromAny(filters.aiGoal));
        setAiModelFilter(toArrFromAny(filters.aiModel));
        setAiDeveloperFilter(toArrFromAny(filters.aiDeveloper));
        setNatSecImminenceFilter(toArrFromAny(filters.natSecImminence));
        setNatSecAutonomyFilter(toArrFromAny(filters.natSecAutonomy));
        setNatSecNoveltyFilter(toArrFromAny(filters.natSecNovelty));

        // Severity thresholds — collect every minSeverity* and minNatSec*
        // key. Empty map clears any prior thresholds; NatSec view restores
        // its canonical Overall ≥ 4 default unless the preset overrides.
        const sev: Record<string, number> = {};
        const nsSev: Record<string, number> = {};
        for (const [k, v] of Object.entries(filters)) {
          if (!k.startsWith('minSeverity') && !k.startsWith('minNatSec')) continue;
          const raw = toStrFromAny(v);
          const n = raw ? parseInt(raw, 10) : NaN;
          if (!Number.isFinite(n) || n <= 0) continue;
          (k.startsWith('minNatSec') ? nsSev : sev)[k] = n;
        }
        setSeverityFilters(sev);
        if (isNatSec && Object.keys(nsSev).length === 0) {
          nsSev.minNatSecOverall = 4;
        }
        setNatSecSeverityFilters(nsSev);

        const nextSortBy = toStrFromAny(filters.sortBy);
        if (nextSortBy && (VALID_SORT_FIELDS as readonly string[]).includes(nextSortBy)) {
          setSortBy(nextSortBy as SortField);
        }
        const nextSortDir = toStrFromAny(filters.sortDir);
        if (nextSortDir === 'desc' || nextSortDir === 'asc') {
          setSortDir(nextSortDir);
        }
        setSearchQuery('');
      },
      resetFilters: handleReset,
    };
    incidentViewRegistry.set(containerId, controller);
    return () => { incidentViewRegistry.delete(containerId); };
  // handleReset / isNatSec capture state setters and props; the setters are
  // stable, and `isNatSec` is derived from a prop that doesn't change after
  // mount, so we register once per containerId.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerId, isNatSec]);

  // "Default" NatSec filter is `{ minNatSecOverall: 4 }`. The reset button
  // shows whenever the state differs from that (including any per-category
  // floor or a different Overall threshold).
  const natSecFiltersAtDefault =
    Object.keys(natSecSeverityFilters).length === 1 && natSecSeverityFilters.minNatSecOverall === 4;

  const hasActiveFilters = [yearFilter, minYearFilter, domainFilter, subdomainFilter, entityFilter, intentFilter, timingFilter, euRiskFilter, aiGoalFilter, aiModelFilter, aiDeveloperFilter, natSecImminenceFilter, natSecAutonomyFilter, natSecNoveltyFilter]
    .some((f) => f.length > 0)
    || Object.values(severityFilters).some((v) => v > 0)
    || (isNatSec ? !natSecFiltersAtDefault : Object.values(natSecSeverityFilters).some((v) => v > 0))
    || searchQuery.length > 0;

  const searched = searchQuery.trim()
    ? incidents.filter((it) => {
        const q = searchQuery.toLowerCase();
        const tail = isNatSec
          ? (IMPACT_LABELS.natsec[it.natSecOverallScore ?? 0] ?? '').toLowerCase()
          : it.subdomain.toLowerCase();
        return (
          it.incidentId.toLowerCase().includes(q) ||
          it.title.toLowerCase().includes(q) ||
          it.date.toLowerCase().includes(q) ||
          tail.includes(q) ||
          String(it.aiSystem ?? '').toLowerCase().includes(q)
        );
      })
    : incidents;

  const parseNumericId = (id: string): number => {
    const n = parseInt(id, 10);
    return isNaN(n) ? Infinity : n;
  };

  const sorted = [...searched].sort((a, b) => {
    let cmp: number;
    if (sortBy === 'incidentId') {
      cmp = parseNumericId(a.incidentId) - parseNumericId(b.incidentId);
    } else if (sortBy === 'highestSeverityScore') {
      cmp = (a.highestSeverityScore ?? -1) - (b.highestSeverityScore ?? -1);
    } else if (sortBy === 'natSecOverallScore' || sortBy === 'natSecResponseLevel') {
      // The level column shows the score's name, so it sorts by the score
      // (an alphabetic sort put Critical first and Substantial last).
      cmp = (a.natSecOverallScore ?? -1) - (b.natSecOverallScore ?? -1);
    } else if (sortBy === 'date') {
      const at = parseDateForSort(a.date);
      const bt = parseDateForSort(b.date);
      // Undated rows (parse -> +Infinity) stay LAST in either direction —
      // without this, descending order would float them to the top.
      if (at === Number.POSITIVE_INFINITY || bt === Number.POSITIVE_INFINITY) {
        return at === bt ? 0 : at === Number.POSITIVE_INFINITY ? 1 : -1;
      }
      cmp = at - bt;
    } else {
      const aVal = String(a[sortBy] ?? '').toLowerCase();
      const bVal = String(b[sortBy] ?? '').toLowerCase();
      cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    }
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  const sortIndicator = (field: SortField) =>
    sortBy === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  const yearDD = useMemo(() => yearOptions.map((v) => ({ value: v, label: v })), [yearOptions]);
  const domainDD = useMemo(() => domainOptions.map((v) => ({ value: v, label: formatTaxonomyLabel(v) })), [domainOptions]);
  const subdomainDD = useMemo(() => subdomainOptions.map((v) => ({ value: v, label: formatTaxonomyLabel(v) })), [subdomainOptions]);
  const entityDD = useMemo(() => entityOptions.map((v) => ({ value: v, label: v })), [entityOptions]);
  const intentDD = useMemo(() => intentOptions.map((v) => ({ value: v, label: v })), [intentOptions]);
  const timingDD = useMemo(() => timingOptions.map((v) => ({ value: v, label: v })), [timingOptions]);
  const euRiskDD = useMemo(() => euRiskOptions.map((v) => ({ value: v, label: v })), [euRiskOptions]);
  const aiGoalDD = useMemo(() => aiGoalOptions.map((v) => ({ value: v, label: v })), [aiGoalOptions]);
  const aiModelDD = useMemo(() => aiModelOptions.map((v) => ({ value: v, label: v })), [aiModelOptions]);
  const aiDeveloperDD = useMemo(() => aiDeveloperOptions.map((v) => ({ value: v, label: v })), [aiDeveloperOptions]);
  const natSecImminenceDD = useMemo(() => natSecImminenceOptions.map((v) => ({ value: v, label: v })), [natSecImminenceOptions]);
  const natSecAutonomyDD = useMemo(() => natSecAutonomyOptions.map((v) => ({ value: v, label: v })), [natSecAutonomyOptions]);
  const natSecNoveltyDD = useMemo(() => natSecNoveltyOptions.map((v) => ({ value: v, label: v })), [natSecNoveltyOptions]);

  // Same shape IncidentChart hands to IncidentFilterBar so the two share a
  // single component for filter chrome.
  const filterDims: FilterDim[] = [];
  if (yearDD.length > 0) {
    filterDims.push({ key: 'year', label: 'Year', options: yearDD, selected: yearFilter, onChange: setYearFilter });
    filterDims.push({ key: 'yearMin', label: 'Year', kind: 'min', options: yearDD, selected: minYearFilter, onChange: setMinYearFilter });
  }
  filterDims.push({ key: 'domain', label: DIM_LABELS.domain, options: domainDD, selected: domainFilter, onChange: setDomainFilter });
  filterDims.push({ key: 'subdomain', label: DIM_LABELS.subdomain, options: subdomainDD, selected: subdomainFilter, onChange: setSubdomainFilter });
  filterDims.push({ key: 'entity', label: DIM_LABELS.entity, options: entityDD, selected: entityFilter, onChange: setEntityFilter });
  filterDims.push({ key: 'intent', label: DIM_LABELS.intent, options: intentDD, selected: intentFilter, onChange: setIntentFilter });
  filterDims.push({ key: 'timing', label: DIM_LABELS.timing, options: timingDD, selected: timingFilter, onChange: setTimingFilter });
  if (aiGoalDD.length > 0) {
    filterDims.push({ key: 'aiGoal', label: DIM_LABELS.aiGoal, options: aiGoalDD, selected: aiGoalFilter, onChange: setAiGoalFilter });
  }
  if (hasAiIdentity && aiModelDD.length > 0) {
    filterDims.push({ key: 'aiModel', label: DIM_LABELS.aiModel, options: aiModelDD, selected: aiModelFilter, onChange: setAiModelFilter, tooltip: DIM_TOOLTIPS.aiModel });
  }
  if (hasAiIdentity && aiDeveloperDD.length > 0) {
    filterDims.push({ key: 'aiDeveloper', label: DIM_LABELS.aiDeveloper, options: aiDeveloperDD, selected: aiDeveloperFilter, onChange: setAiDeveloperFilter, tooltip: DIM_TOOLTIPS.aiDeveloper });
  }
  if (euRiskDD.length > 0) {
    filterDims.push({ key: 'euRisk', label: DIM_LABELS.euRisk, options: euRiskDD, selected: euRiskFilter, onChange: setEuRiskFilter, tooltip: DIM_TOOLTIPS.euRisk });
  }
  // NatSec-only threat-characteristic filters (Imminence / Autonomy / Novelty).
  if (isNatSec) {
    if (natSecImminenceDD.length > 0) {
      filterDims.push({ key: 'natSecImminence', label: 'Imminence', options: natSecImminenceDD, selected: natSecImminenceFilter, onChange: setNatSecImminenceFilter });
    }
    if (natSecAutonomyDD.length > 0) {
      filterDims.push({ key: 'natSecAutonomy', label: 'Autonomy', options: natSecAutonomyDD, selected: natSecAutonomyFilter, onChange: setNatSecAutonomyFilter });
    }
    if (natSecNoveltyDD.length > 0) {
      filterDims.push({ key: 'natSecNovelty', label: 'Novelty', options: natSecNoveltyDD, selected: natSecNoveltyFilter, onChange: setNatSecNoveltyFilter });
    }
  }
  // NatSec view: the Overall Score floor + per-category floors are surfaced
  // via a dedicated severity-style popover (rendered next to the standard
  // Severity pill below), not as a chip on this row.
  // "AI System" column: standard view, desktop, datasets with identity
  // cells only. One line per row (name + version + variant, developer in
  // parentheses); the detail modal has the full breakdown.
  const showAiSystem = hasAiIdentity && !isMobile && !isNatSec;
  const gridTemplateColumns = isMobile
    ? '60px 1fr 100px'
    : isNatSec
      ? '60px 1fr 110px 160px'
      : showAiSystem
        ? '60px 1fr 100px 200px 170px 130px'
        : '60px 1fr 100px 220px 130px';

  const natSecSeverityFieldDefs = isNatSec
    ? [
        { key: 'natSecPhysicalScore',    label: 'Physical Security & Critical Infrastructure', paramKey: 'minNatSecPhysical' },
        { key: 'natSecInformationScore', label: 'Information Warfare & Intelligence',          paramKey: 'minNatSecInformation' },
        { key: 'natSecSovereigntyScore', label: 'Sovereignty & Government Functions',          paramKey: 'minNatSecSovereignty' },
        { key: 'natSecEconomicScore',    label: 'Economic & Technological Security',           paramKey: 'minNatSecEconomic' },
        { key: 'natSecSocietalScore',    label: 'Societal Stability & Human Rights',           paramKey: 'minNatSecSocietal' },
      ]
    : undefined;

  return (
    <div style={{ position: 'relative', background: 'transparent', color: 'inherit', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Filter bar — same chip-and-popover pattern as the charts. The
          incident list has no stack-by dimension, so an empty availableStackBy
          hides the Stack-by control. */}
      <IncidentFilterBar
        availableStackBy={[]}
        stackBy={null}
        onStackByChange={() => { /* not used; no stack-by on the list view */ }}
        stackByLabels={DIM_LABELS}
        stackByTooltips={DIM_TOOLTIPS}
        dimensions={filterDims}
        severityFields={severityFields}
        severityFilters={severityFilters}
        onSeverityChange={setSeverityFilters}
        // On the NatSec view two severity controls sit side by side, so
        // each is named for what it measures: harm vs national-security
        // impact. The standard view has only one, where "Severity" is
        // unambiguous.
        severityLabel={isNatSec ? 'Harm Severity' : 'Severity'}
        natSecSeverityLabel="NatSec Impact"
        natSecSeverityFields={natSecSeverityFieldDefs}
        natSecSeverityFilters={isNatSec ? natSecSeverityFilters : undefined}
        onNatSecSeverityChange={isNatSec ? setNatSecSeverityFilters : undefined}
        hasActiveFilter={hasActiveFilters}
        onResetAll={handleReset}
        isMobile={isMobile}
      />

      {/* Search + count */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        // Wrap on narrow screens: the "Showing N of M incidents" counter
        // drops to its own line instead of clipping to "incide...".
        flexWrap: 'wrap',
        gap: 12,
        rowGap: 4,
        marginBottom: 8,
      }}>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <input
            type="text"
            value={searchQuery}
            onInput={(e) => setSearchQuery((e.target as HTMLInputElement).value)}
            placeholder="Search incidents..."
            style={{
              padding: '4px 8px',
              paddingRight: searchQuery ? 24 : 8,
              fontSize: 11,
              border: '1px solid var(--gc-control-border, currentColor)',
              borderRadius: 9,
              background: 'transparent',
              color: 'inherit',
              width: isMobile ? 160 : 200,
              outline: 'none',
              opacity: 0.8,
            }}
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              style={{
                position: 'absolute',
                right: 6,
                top: '50%',
                transform: 'translateY(-50%)',
                border: 'none',
                background: 'none',
                color: 'var(--gc-text-muted, #6b7280)',
                cursor: 'pointer',
                fontSize: 12,
                padding: 0,
                lineHeight: 1,
              }}
              title="Clear search"
            >
              ×
            </button>
          )}
        </div>
        <div style={{ fontSize: 12, color: 'var(--gc-text-muted, #6b7280)', whiteSpace: 'nowrap' }}>
          {listLoading ? 'Loading...' : (
            searchQuery.trim()
              ? `${sorted.length} of ${incidents.length} incidents${totalIncidentsAll != null ? ` (${totalIncidentsAll} total)` : ''}`
              : `Showing ${incidents.length}${totalIncidentsAll != null && incidents.length !== totalIncidentsAll ? ` of ${totalIncidentsAll}` : ''} incidents`
          )}
        </div>
      </div>

      {/* Table — gc-incident-list class lets the embed's injectStyles
          enforce border + row dividers with !important, so Webflow's
          global form/div resets can't strip them. */}
      <div className="gc-incident-list" style={{
        border: '1px solid var(--gc-border, #d1d5db)',
        borderRadius: 6,
        overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      }}>
        <div style={{ maxHeight: 500, overflowY: 'auto' }}>
          <div className="gc-incident-list-head" style={{
            display: 'grid',
            gridTemplateColumns,
            gap: 0,
            padding: '8px 12px',
            background: 'var(--gc-surface-muted, #f1f5f9)',
            borderBottom: '1px solid var(--gc-border, #d1d5db)',
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--gc-text-muted, #6b7280)',
            userSelect: 'none',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}>
            <div onClick={() => handleSort('incidentId')} style={{ cursor: 'pointer' }}>
              ID{sortIndicator('incidentId')}
            </div>
            <div onClick={() => handleSort('title')} style={{ cursor: 'pointer' }}>
              Incident Title{sortIndicator('title')}
            </div>
            {!isMobile && (
              <div
                onClick={() => handleSort('date')}
                style={{ cursor: 'pointer', textAlign: isNatSec ? 'center' : 'left' }}
              >
                Incident Date{sortIndicator('date')}
              </div>
            )}
            {!isMobile && !isNatSec && (
              <div
                onClick={() => handleSort('subdomain')}
                style={{ cursor: 'pointer' }}
                title="Click into incidents to see additional subdomains associated with each incident"
              >
                Primary Risk Subdomain{sortIndicator('subdomain')}
              </div>
            )}
            {showAiSystem && (
              <div
                onClick={() => handleSort('aiSystem')}
                style={{ cursor: 'pointer' }}
                title="The AI system(s) the incident report names — model name, version and variant, with the developer in brackets — normalised against Arcola’s registry. Click into an incident for the full breakdown. “Unidentified” = AI was involved but the report does not say which; “None named” = no AI system is mentioned."
              >
                AI System{sortIndicator('aiSystem')}
              </div>
            )}
            {isNatSec ? (
              <div
                onClick={() => handleSort('natSecResponseLevel')}
                style={{ cursor: 'pointer', textAlign: 'center' }}
              >
                Overall NatSec Level{sortIndicator('natSecResponseLevel')}
              </div>
            ) : (
              <div
                onClick={() => handleSort('highestSeverityScore')}
                style={{ cursor: 'pointer', textAlign: 'center' }}
                title="The severity of the harm category with the highest rating"
              >
                Highest Severity{sortIndicator('highestSeverityScore')}
              </div>
            )}
          </div>

          {listLoading ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--gc-text-muted, #6b7280)', fontSize: 13 }}>
              Loading...
            </div>
          ) : sorted.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--gc-text-muted, #6b7280)', fontSize: 13 }}>
              No incidents found matching the current filters.
            </div>
          ) : (
            sorted.map((it) => {
              // A real link to the incident's page when pages are live, so
              // crawlers (and a middle-click) reach it; a plain click still
              // opens the modal.
              const href = incidentPageUrl(it.incidentId, it.title || '', dataset);
              const Row = href ? 'a' : 'div';
              return (
              <Row
                key={it.incidentId}
                className="gc-incident-row"
                href={href || undefined}
                onClick={(e: MouseEvent) => {
                  if (href && (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) return;
                  e.preventDefault();
                  handleIncidentClick(it.incidentId);
                }}
                style={{
                  display: 'grid',
                  gridTemplateColumns,
                  alignItems: 'center',
                  padding: '6px 12px',
                  borderBottom: '1px solid var(--gc-border, #d1d5db)',
                  fontSize: 12,
                  cursor: 'pointer',
                  color: 'inherit',
                  textDecoration: 'none',
                }}
              >
                <div style={{ color: 'var(--gc-link, inherit)', fontWeight: 500 }}>{it.incidentId}</div>
                <div style={{
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  lineHeight: '1.4',
                  paddingRight: 8,
                }}>
                  {it.title || '—'}
                </div>
                {!isMobile && (
                  <div style={{
                    color: 'var(--gc-text-muted, #6b7280)',
                    whiteSpace: 'nowrap',
                    textAlign: isNatSec ? 'center' : 'left',
                  }}>
                    {it.date || '—'}
                  </div>
                )}
                {!isMobile && !isNatSec && (
                  <div style={{
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    lineHeight: '1.4',
                    color: 'var(--gc-text-muted, #6b7280)',
                  }}>
                    {it.subdomain ? formatTaxonomyLabel(it.subdomain) : '—'}
                  </div>
                )}
                {showAiSystem && (
                  <div style={{
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    lineHeight: '1.4',
                    paddingRight: 8,
                    // The two evidenced-but-empty states read as notes, not
                    // names — muted so a real system name stands out.
                    color: it.aiSystem === 'Unidentified' || it.aiSystem === 'None named'
                      ? 'var(--gc-text-muted, #6b7280)'
                      : 'inherit',
                  }}>
                    {it.aiSystem || '—'}
                  </div>
                )}
                <div style={{ textAlign: 'center' }}>
                  {isNatSec
                    ? <ImpactPill score={it.natSecOverallScore ?? null} size={isMobile ? 'xs' : 'sm'} />
                    : <ImpactPill score={it.highestSeverityScore ?? null} variant="standard" size={isMobile ? 'xs' : 'sm'} />}
                </div>
              </Row>
              );
            })
          )}
        </div>
      </div>

      {isNatSec ? (
        <NatSecIncidentDetailModal
          isOpen={detailOpen}
          onClose={handleDetailClose}
          incidentId={detailId}
          incident={detailIncident}
          loading={detailLoading}
        />
      ) : (
        <IncidentDetailModal
          isOpen={detailOpen}
          onClose={handleDetailClose}
          incidentId={detailId}
          incident={detailIncident}
          loading={detailLoading}
        />
      )}
    </div>
  );
}
